import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { StockAlertService } from '../../../shared/services/stock-alert.service';
import { StockAlertStore } from '../../../shared/services/stock-alert.store';
import { StockAlertError } from '../../../shared/models/stock-alert.model';

type UnsubscribeStatus = 'pending' | 'success' | 'not_found' | 'error';

@Component({
  selector: 'app-stock-alert-unsubscribe-page',
  imports: [ButtonComponent, RouterLink],
  templateUrl: './stock-alert-unsubscribe-page.component.html',
  styleUrl: './stock-alert-unsubscribe-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class StockAlertUnsubscribePageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stockAlertService = inject(StockAlertService);
  private readonly stockAlertStore = inject(StockAlertStore);

  protected readonly status = signal<UnsubscribeStatus>('pending');

  constructor() {
    this.submit();
  }

  protected retry(): void {
    this.status.set('pending');
    this.submit();
  }

  private submit(): void {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.status.set('not_found');
      return;
    }

    this.stockAlertService
      .unsubscribe(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.stockAlertStore.removeByToken(token);
          this.status.set('success');
        },
        error: (err) => {
          if (err instanceof StockAlertError && err.reason === 'token_not_found') {
            this.stockAlertStore.removeByToken(token);
            this.status.set('not_found');
          } else {
            this.status.set('error');
          }
        },
      });
  }
}
